import { ValidStandard } from "../api/IsoApiServer";

/* global Word console */
export interface InvalidOccurrence {
  id: string;
  text: string;
  label: string;
  message: string;
  ordinal: number;
  position: number;
  validToString: string;
  anotation?: string;
  description?: string;
  detailUrl?: string;
  validStandards?: ValidStandard[];
}

/**
 * Filters search results to only include exact matches.
 * An exact match is followed by a space, tab, newline, or end of text.
 * Any other character after the match means it's part of a longer standard.
 */
async function getExactMatches(
  context: Word.RequestContext,
  searchResults: Word.RangeCollection
): Promise<Word.Range[]> {
  if (searchResults.items.length === 0) {
    return [];
  }

  // Load all paragraphs and match text
  for (const match of searchResults.items) {
    match.paragraphs.load("items");
    match.load("text");
  }
  await context.sync();

  for (const match of searchResults.items) {
    const paragraph = match.paragraphs.items[0];
    if (paragraph) {
      paragraph.load("text");
    }
  }
  await context.sync();

  const exactMatches: Word.Range[] = [];

  for (const match of searchResults.items) {
    const paragraph = match.paragraphs.items[0];
    if (!paragraph) {
      exactMatches.push(match); // Can't verify, include it
      continue;
    }

    const paragraphText = paragraph.text;
    const matchText = match.text;

    // Find this match in the paragraph and check what follows
    let searchStart = 0;
    let isExact = false;

    while (searchStart < paragraphText.length) {
      const matchIndex = paragraphText.indexOf(matchText, searchStart);
      if (matchIndex === -1) break;

      const charAfterMatch = paragraphText[matchIndex + matchText.length];

      // Exact match only if followed by space, tab, newline, or end of text
      if (!charAfterMatch || /^[\s\t\n\r]$/.test(charAfterMatch)) {
        isExact = true;
        break;
      }

      searchStart = matchIndex + 1;
    }

    if (isExact) {
      exactMatches.push(match);
    }
  }

  return exactMatches;
}

export async function getDocumentText(): Promise<string> {
  return Word.run(async (context) => {
    const body = context.document.body;
    body.load("text");
    await context.sync();
    return body.text ?? "";
  });
}


// export async function validateDocument(): Promise<InvalidOccurrence[]> {
//   try {
//     const documentText = await getDocumentText();
//     return mockValidate(documentText);
//   } catch (error) {
//     console.log("Validation error:", error);
//     return [];
//   }
// }

export async function highlightInvalidOccurrence(target: InvalidOccurrence, ms = 2000): Promise<void> {
  await Word.run(async (context) => {
    const searchResults = context.document.body.search(target.text, {
      matchCase: false,
      matchWholeWord: false,
    });
    searchResults.load("items");
    await context.sync();

    // Filter to only exact matches (not substrings of longer standards)
    const exactMatches = await getExactMatches(context, searchResults);
    
    const match = exactMatches[target.ordinal];
    if (!match) {
      console.log(`Unable to highlight occurrence ${target.id}.`);
      return;
    }

    // Move the cursor to the start of the match and shift focus to the editor
    match.select("Select");
    await context.sync();

    // Load existing highlight value
    match.font.load("highlightColor");
    await context.sync();
    const prev = match.font.highlightColor ?? null;

    // Apply temporary highlight
    match.font.highlightColor = "#FFFF00";
    await context.sync();

    // Wait, then restore previous highlight (or clear)
    match.select("Start");
    await context.sync();
    await new Promise((r) => setTimeout(r, ms));
    match.font.highlightColor = prev;
    await context.sync();
    match.select("Start");
    await context.sync();
  });
}
// export async function highlightInvalidOccurrence(target: InvalidOccurrence): Promise<void> {
//   function ensureXmlSpaceOnText(ooxml: string) {
//     // add xml:space="preserve" to w:t that contain leading/trailing spaces
//     return ooxml.replace(/<w:t([^>]*)>(\s.*?<\/w:t>)/g, (m, attrs, rest) => {
//       if (/\bxml:space=/.test(attrs)) return `<w:t${attrs}>${rest}`;
//       return `<w:t${attrs} xml:space="preserve">${rest}`;
//     }).replace(/<w:t([^>]*)>(.*?\s)<\/w:t>/g, (m, attrs, inner) => {
//       if (/\bxml:space=/.test(attrs)) return `<w:t${attrs}>${inner}</w:t>`;
//       return `<w:t${attrs} xml:space="preserve">${inner}</w:t>`;
//     });
//   }

//   const backgroundColor = "FFFF00"; // hex without '#', e.g. yellow
//   const shdTag = `<w:shd w:val="clear" w:color="auto" w:fill="${backgroundColor}"/>`;

//   await Word.run(async (context) => {
//     const searchResults = context.document.body.search(target.text, {
//       matchCase: false,
//       matchWholeWord: false,
//     });
//     searchResults.load("items");
//     await context.sync();

//     const match = searchResults.items[target.ordinal];
//     if (!match) {
//       console.log(`Unable to highlight occurrence ${target.id}.`);
//       return;
//     }

//     // get the OOXML for the matched range
//     const ooxmlResult = match.getOoxml();
//     await context.sync();
//     let ooxml = ooxmlResult.value ?? "";

//     if (!ooxml) {
//       // fallback: build a simple run OOXML (keeps text but may lose formatting)
//       const escapeXml = (s: string) =>
//         s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
//           .replace(/"/g, "&quot;").replace(/'/g, "&apos;");
//       const text = escapeXml((await (async () => { match.load("text"); await context.sync(); return match.text ?? ""; })()) as any);
//       ooxml = `<w:r xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:rPr>${shdTag}</w:rPr><w:t xml:space="preserve">${text}</w:t></w:r>`;
//     } else {
//       // ensure runs have shading: insert <w:shd/> into every <w:rPr>, or add <w:rPr> before <w:t> when missing
//       // 1) inject into existing <w:rPr>
//       ooxml = ooxml.replace(/<w:rPr([^>]*)>/g, (m, attrs) => {
//         if (m.includes("<w:shd")) return m; // already has shading
//         return `<w:rPr${attrs}>${shdTag}`;
//       });

//       // 2) for runs without rPr, add one before the first w:t in the run
//       ooxml = ooxml.replace(/<w:r([^>]*)>(\s*(?:<(?:w:instrText|w:fldSimple|w:t)[\s\S]*?>))/g, (m, rAttrs, after) => {
//         // if r already had rPr inserted by previous step, skip
//         if (m.includes("<w:rPr")) return m;
//         return `<w:r${rAttrs}><w:rPr>${shdTag}</w:rPr>${after}`;
//       });

//       // 3) ensure spaces are preserved where necessary
//       ooxml = ensureXmlSpaceOnText(ooxml);
//     }

//     try {
//       match.insertOoxml(ooxml, Word.InsertLocation.replace);
//       await context.sync();
//     } catch (err) {
//       console.log("OOXML insert failed:", err);
//     }
//   });
// }
export async function replaceInvalidOccurrence(
  target: InvalidOccurrence,
  replacementText: string,
  replaceAll = false
): Promise<void> {
  await Word.run(async (context) => {
    if (!replaceAll) {
      const searchResults = context.document.body.search(target.text, {
        matchCase: false,
        matchWholeWord: false,
      });
      searchResults.load("items");
      await context.sync();

      // Filter to only exact matches (not substrings of longer standards)
      const exactMatches = await getExactMatches(context, searchResults);
      
      const match = exactMatches[target.ordinal];
      if (!match) {
        console.log(`Unable to replace occurrence ${target.id}.`);
        return;
      }
      match.insertText(replacementText, Word.InsertLocation.replace);
      await context.sync();
      return;
    }

    // replaceAll: loop, re-search each iteration and replace the last match
    while (true) {
      const searchResults = context.document.body.search(target.text, {
        matchCase: false,
        matchWholeWord: false,
      });
      searchResults.load("items");
      await context.sync();

      // Filter to only exact matches (not substrings of longer standards)
      const exactMatches = await getExactMatches(context, searchResults);
      
      if (exactMatches.length === 0) break;

      // pick last match to avoid shifting earlier matches
      const last = exactMatches[exactMatches.length - 1];
      last.insertText(replacementText, Word.InsertLocation.replace);
      await context.sync();

      // Safety: avoid infinite loop if replacementText still matches target.text
      if (replacementText.toLowerCase().includes(target.text.toLowerCase())) {
        console.warn("Replacement text contains original text — stopping to avoid infinite loop.");
        break;
      }
    }
  });
}
export async function insertText(text: string): Promise<void> {
  // ...existing code...
  try {
    await Word.run(async (context) => {
      const body = context.document.body;
      body.insertParagraph(text, Word.InsertLocation.end);
      await context.sync();
    });
  } catch (error) {
    console.log("Error: " + error);
  }
}
